import { Button } from '@deepguide-ai/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@deepguide-ai/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@deepguide-ai/ui/popover';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';

interface ProfileTypeComboboxProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const SUGGESTED_PROFILE_TYPES = [
  { value: 'Administrator', label: 'Administrator' },
  { value: 'Manager', label: 'Manager' },
  { value: 'Auditor', label: 'Auditor' },
  { value: 'Compliance Officer', label: 'Compliance Officer' },
  { value: 'Security Analyst', label: 'Security Analyst' },
  { value: 'IT Administrator', label: 'IT Administrator' },
  { value: 'Finance Manager', label: 'Finance Manager' },
  { value: 'Operations Manager', label: 'Operations Manager' },
];

export function ProfileTypeCombobox({ value, onChange, placeholder = 'Select or enter profile type...' }: ProfileTypeComboboxProps) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  // Filter suggestions based on search
  const filteredSuggestions = SUGGESTED_PROFILE_TYPES.filter((type) =>
    type.label.toLowerCase().includes(searchValue.toLowerCase())
  );

  // Check if current value is a custom value (not in suggestions)
  const isCustomValue = value && !SUGGESTED_PROFILE_TYPES.some((type) => type.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          <span className={cn('truncate', !value && 'text-muted-foreground')}>
            {value || placeholder}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[462px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search or type custom type..."
            value={searchValue}
            onValueChange={setSearchValue}
          />
          <CommandList>
            {searchValue && !filteredSuggestions.some((type) => type.value === searchValue) && (
              <CommandGroup heading="Custom">
                <CommandItem
                  value={searchValue}
                  onSelect={(currentValue) => {
                    onChange(currentValue);
                    setOpen(false);
                    setSearchValue('');
                  }}
                >
                  <div className="flex w-full items-center justify-between gap-2">  
                    Use "{searchValue}"
                    <Check className={cn('h-4 w-4', value === searchValue ? 'opacity-100' : 'opacity-0')} />
                  </div>
                </CommandItem>
              </CommandGroup>
            )}
            {filteredSuggestions.length > 0 ? (
              <CommandGroup heading="Suggested Types">
                {filteredSuggestions.map((type) => (
                  <CommandItem
                    key={type.value}
                    value={type.value}
                    onSelect={(currentValue) => {
                      onChange(currentValue);
                      setOpen(false);
                      setSearchValue('');
                    }}
                  >
                    <div className="flex w-full items-center justify-between gap-2">  
                    {type.label}
                    <Check className={cn('mr-2 h-4 w-4', value === type.value ? 'opacity-100' : 'opacity-0')} />
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : (
              !searchValue && <CommandEmpty>Start typing to add custom profile type</CommandEmpty>
            )}
            {isCustomValue && value && (
              <CommandGroup heading="Current">
                <CommandItem
                  value={value}
                  onSelect={(currentValue) => {
                    onChange(currentValue);
                    setOpen(false);
                    setSearchValue('');
                  }}
                >
                  <div className="flex w-full items-center justify-between gap-2">  
                    <Check className="mr-2 h-4 w-4 opacity-100" />
                    {value}
                  </div>
                  {value}
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
